import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Search } from "lucide-react";
import { http } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/format";

export default function SearchOverlay() {
  const { searchOpen, setSearchOpen } = useStore();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const inputRef = useRef();

  useEffect(() => { if (searchOpen) setTimeout(() => inputRef.current?.focus(), 80); }, [searchOpen]);

  useEffect(() => {
    if (!q) { setRes(null); return; }
    const t = setTimeout(() => {
      http.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setRes(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [setSearchOpen]);

  if (!searchOpen) return null;
  const close = () => { setSearchOpen(false); setQ(""); setRes(null); };

  return (
    <div className="fixed inset-0 z-[60] bg-[#070708]/98 backdrop-blur-sm" data-testid="search-overlay">
      <div className="max-w-3xl mx-auto px-4 pt-20">
        <div className="flex items-center gap-3 border-b-2 border-zinc-700 pb-4">
          <Search className="text-zinc-500" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search products, collections..." data-testid="search-input"
            className="flex-1 bg-transparent text-2xl sm:text-3xl font-display tracking-wide uppercase placeholder:text-zinc-700 outline-none text-white" />
          <button onClick={close} aria-label="Close search" data-testid="search-close-btn"><X className="text-zinc-400" /></button>
        </div>

        {res && (
          <div className="mt-8 space-y-8 max-h-[70vh] overflow-y-auto">
            {res.products?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Products</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {res.products.map((p) => (
                    <Link key={p.id} to={`/product/${p.slug}`} onClick={close} className="group" data-testid={`search-result-${p.slug}`}>
                      <div className="aspect-[3/4] overflow-hidden bg-zinc-900">
                        <img src={p.images?.[0]?.url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </div>
                      <p className="text-xs mt-2 text-zinc-200 line-clamp-1">{p.name}</p>
                      <p className="text-xs font-semibold text-white">{inr(p.effective_price)}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {res.collections?.length > 0 && (
              <div>
                <p className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">Collections</p>
                <div className="flex flex-wrap gap-2">
                  {res.collections.map((c) => (
                    <Link key={c.id} to={`/collection/${c.slug}`} onClick={close}
                      className="border border-zinc-800 px-4 py-2 text-sm hover:border-accent-ss">{c.name}</Link>
                  ))}
                </div>
              </div>
            )}
            {q && !res.products?.length && !res.collections?.length && (
              <p className="text-zinc-500">No results for "{q}". Try something else.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
